import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link } from "wouter";
import { getLoginUrl } from "@/const";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { trpc } from "@/lib/trpc";
import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import {
  Gamepad2, Trophy, Users, TrendingUp, DollarSign, Shield,
  ArrowRight, ChefHat, Dumbbell, Palette, Music, Heart,
  Building2, GraduationCap, Globe, Mic2, Camera, Code,
  Scissors, Brain, Bike, Dog, Leaf, Shirt, Wrench,
  UserPlus, Search, Award, Zap, Share2, Star,
  HelpCircle, CheckCircle2,
} from "lucide-react";

/* ─── The three roles (shown first so every visitor knows who's who) ─── */
const roleCards = [
  {
    key: "creator",
    label: "Creator",
    icon: Gamepad2,
    color: "text-gold",
    ring: "border-gold/30 bg-gold/10",
    what: "The host, like a tournament organizer: picks the skill, sets the rules and the ticket price.",
    short: "Hosts it · earns 10–15%",
    gets: "Earns 10% of ticket sales (up to 15%)",
  },
  {
    key: "promoter",
    label: "Promoter",
    icon: Share2,
    color: "text-blue-400",
    ring: "border-blue-400/30 bg-blue-400/10",
    what: "The ticket seller, like a concert promoter: shares a personal link and brings the players.",
    short: "Sells tickets · earns 10%",
    gets: "Shares the 10% promoter cut, by tickets sold",
  },
  {
    key: "player",
    label: "Player",
    icon: Trophy,
    color: "text-neon",
    ring: "border-neon/30 bg-neon/10",
    what: "The competitor: buys a ticket, plays the same rounds as everyone else and posts a real score. 18+.",
    short: "Competes · winners split 65%",
    gets: "Winners take 65% of the prize pool",
  },
];

const splitBar = [
  { label: "Winners", pct: 65, cls: "bg-neon text-background" },
  { label: "Creator", pct: 10, cls: "bg-gold text-background" },
  { label: "Promoters", pct: 10, cls: "bg-blue-400 text-background" },
  { label: "GamePoint", pct: 15, cls: "bg-navy-light text-foreground" },
];

/* ─── Use Cases (expanded to 18+) ─── */
const useCases = [
  { icon: ChefHat, title: "Cooking Competitions", desc: "Chefs compete with signature dishes. Community votes on taste, presentation, creativity. Ticket sales fund the prize pool.", example: "A BBQ restaurant in Texas hosts a $20 ticket rib cook-off. 200 tickets = $4,000 prize pool." },
  { icon: Gamepad2, title: "Esports Tournaments", desc: "From CS:GO to Fortnite, host competitive gaming events with real cash prizes for top players.", example: "A Twitch streamer hosts a $50 Valorant tournament. 100 players = $5,000 prize pool." },
  { icon: Dumbbell, title: "Fitness Challenges", desc: "Push-up contests, transformation battles, marathon challenges. Prove your fitness for real money.", example: "A gym in LA hosts a 30-day transformation challenge. $25 ticket, 80 members = $2,000 pool." },
  { icon: Palette, title: "Art Contests", desc: "Digital art, painting, photography. Artists submit work, community votes, winners earn cash.", example: "An art teacher hosts a student showcase. $10 ticket, 50 students = $500 in prizes." },
  { icon: Music, title: "Music Battles", desc: "Beat-making, freestyle rap, singing competitions. Musicians compete for cash and exposure.", example: "A producer hosts a beat battle on Instagram. $15 ticket, 60 producers = $900 prize pool." },
  { icon: Heart, title: "Charity Fundraisers", desc: "Host competitions where a portion of every ticket goes directly to your chosen cause.", example: "A church hosts a bake-off fundraiser. $10 ticket, 100 participants, 50% to charity = $500 raised." },
  { icon: Building2, title: "Corporate Team Building", desc: "Companies host internal competitions — trivia, hackathons, sales contests with real incentives.", example: "A tech company hosts a coding challenge. Company-funded prizes, 50 engineers compete." },
  { icon: GraduationCap, title: "Education Competitions", desc: "Spelling bees, math olympiads, science fairs. Students compete and learn while earning prizes.", example: "A university hosts a debate tournament. $5 ticket, 200 students = $1,000 scholarship pool." },
  { icon: Code, title: "Coding Challenges", desc: "Algorithm battles, app-building sprints, bug bounties. Developers prove their skills for cash.", example: "A bootcamp hosts a 48-hour hackathon. $30 ticket, 40 teams = $1,200 prize pool." },
  { icon: Camera, title: "Photography Contests", desc: "Street photography, portraits, landscapes. Photographers submit their best shots for cash prizes.", example: "A camera brand sponsors a nature photo contest. $15 ticket, 150 photographers = $2,250 pool." },
  { icon: Mic2, title: "Podcast Competitions", desc: "Best episode, best interview, best storytelling. Podcasters compete for audience votes and prizes.", example: "A podcast network hosts a 'Best New Episode' contest. $20 ticket, 30 shows = $600 pool." },
  { icon: Brain, title: "Trivia & Quiz Nights", desc: "Host pub trivia, general knowledge, or niche topic quizzes with tickets and cash prizes.", example: "A bar hosts weekly trivia. $5 ticket per team, 20 teams = $100 weekly prize." },
  { icon: Dog, title: "Pet Competitions", desc: "Cutest pet, best trick, agility courses. Pet owners compete and show off their furry friends.", example: "A pet store hosts a 'Cutest Dog' contest. $10 ticket, 80 dogs = $800 prize pool." },
  { icon: Scissors, title: "Craft & DIY Contests", desc: "Woodworking, knitting, home improvement. Makers compete with their handmade creations.", example: "A hardware store hosts a birdhouse building contest. $15 ticket, 40 makers = $600 pool." },
  { icon: Bike, title: "Sports Challenges", desc: "Cycling, running, swimming, basketball. Athletes compete in timed or scored events for cash.", example: "A cycling club hosts a virtual race. $20 ticket, 100 riders = $2,000 prize pool." },
  { icon: Leaf, title: "Gardening Competitions", desc: "Best garden, biggest tomato, most creative landscape. Green thumbs compete for bragging rights and cash.", example: "A community garden hosts a harvest festival contest. $10 ticket, 60 gardeners = $600 pool." },
  { icon: Shirt, title: "Fashion Design", desc: "Design challenges, outfit styling, sustainable fashion. Designers compete for cash and exposure.", example: "A fashion school hosts a sustainable design challenge. $25 ticket, 30 students = $750 pool." },
  { icon: Wrench, title: "Trade Skills", desc: "Welding, plumbing, electrical. Tradespeople compete to showcase their expertise for real prizes.", example: "A trade school hosts a welding competition. $20 ticket, 25 welders = $500 prize pool." },
  { icon: Globe, title: "Global Events", desc: "Anyone, anywhere can host or join. No borders, no limits. Compete with the world.", example: "A creator in Nigeria hosts a cooking contest. Players from 12 countries join. $10 ticket, 150 players." },
];

/* ─── Features ─── */
const features = [
  { icon: Gamepad2, title: "Any Competition, Any Niche", desc: "Cooking, coding, fitness, art, trivia — if it can be judged, it can be a GamePoint game. Infinite categories." },
  { icon: Shield, title: "Secure Tickets & Prizes", desc: "Ticket money is held in escrow and paid out by the creator's rules when results post." },
  { icon: Share2, title: "Promoter Links", desc: "Every member gets a personal promoter link. Bring players and share in the promoter pool, pro-rata to the players you bring." },
  { icon: DollarSign, title: "Checked Results", desc: "Top scores are checked before results post, and any player can file a dispute from the event page." },
  { icon: Zap, title: "AI-Powered Event Creation", desc: "Our AI helps you design clearly structured, fairly judged events optimized for viral growth and fair play." },
  { icon: Star, title: "Four Ways to Earn", desc: "Platform earns fees, creators earn host share, promoters earn commission, and players win prizes. Everyone profits." },
];

/* ─── Prize Pool Calculator ─── */
function PrizeCalculator() {
  const [entryFee, setEntryFee] = useState(20);
  const [players, setPlayers] = useState(100);
  const [creatorPct, setCreatorPct] = useState(10);
  const [promoterPct, setPromoterPct] = useState(10);

  const PLATFORM_FEE = 15;
  const total = entryFee * players;
  const platformFee = total * (PLATFORM_FEE / 100);
  const distributablePool = total - platformFee;
  const creatorEarn = total * (creatorPct / 100);
  const promoterEarn = total * (promoterPct / 100);
  const winnerEarn = distributablePool - creatorEarn - promoterEarn;

  return (
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-extrabold text-center text-foreground mb-4">
          Prize Pool Calculator
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          See exactly how the money flows. The event creator sets the split between creator share, promoter pool, and player prizes. The platform takes a flat 15% fee.
        </p>
        <div className="glass-panel rounded-2xl p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Ticket price</label>
                  <div className="flex items-center gap-1 bg-navy-light border border-dashed border-neon/40 rounded-lg px-3 py-2">
                    <span className="text-muted-foreground">$</span>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={entryFee}
                      onChange={(e) => setEntryFee(Math.min(10000, Math.max(1, Number(e.target.value))))}
                      className="bg-transparent w-full text-foreground focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Players</label>
                  <input
                    type="number"
                    value={players}
                    onChange={(e) => setPlayers(Math.max(2, Number(e.target.value)))}
                    className="w-full bg-navy-light border border-dashed border-neon/40 rounded-lg px-3 py-2 text-foreground focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                  Creator Share
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Percent of ticket sales paid to the event creator.
                    </TooltipContent>
                  </Tooltip>
                </label>
                <input
                  type="range" min={0} max={15} value={creatorPct}
                  onChange={(e) => setCreatorPct(Number(e.target.value))}
                  className="w-full accent-neon"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0%</span>
                  <span className="text-neon">{creatorPct}% (${creatorEarn.toFixed(2)})</span>
                  <span>15%</span>
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                  Promoter Pool
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Shared pro-rata among promoters based on tickets sold.
                    </TooltipContent>
                  </Tooltip>
                </label>
                <input
                  type="range" min={0} max={15} value={promoterPct}
                  onChange={(e) => setPromoterPct(Number(e.target.value))}
                  className="w-full accent-neon"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0%</span>
                  <span className="text-neon">{promoterPct}% (${promoterEarn.toFixed(2)})</span>
                  <span>15%</span>
                </div>
              </div>
              <div className="bg-navy-light rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total ticket sales:</span>
                  <span className="font-bold text-foreground">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Platform Fee (15%):</span>
                  <span className="font-bold text-muted-foreground">${platformFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Creator Earnings:</span>
                  <span className="font-bold text-blue-400">${creatorEarn.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Promoter Pool:</span>
                  <span className="font-bold text-pink-400">${promoterEarn.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-border/30 pt-2">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    Total Player Prizes
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3.5 h-3.5 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Amount available for winners after platform, creator,
                        and promoter shares.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <span className="font-bold text-neon text-lg">${winnerEarn.toFixed(2)}</span>
                </div>
              </div>
            </div>
            {/* Pie chart visualization */}
            <div className="flex flex-col items-center gap-4">
              <svg viewBox="0 0 200 200" className="w-48 h-48">
                {(() => {
                  const visibleTotal = (100 - PLATFORM_FEE);
                  const winPct = (visibleTotal - creatorPct - promoterPct) / visibleTotal;
                  const hPct = creatorPct / visibleTotal;
                  const pPct = promoterPct / visibleTotal;
                  const r = 80;
                  const cx = 100, cy = 100;
                  const slices = [
                    { pct: winPct, color: "#0ea5e9", label: "Winners" },
                    { pct: hPct, color: "#3b82f6", label: "Creator" },
                    { pct: pPct, color: "#ec4899", label: "Promoters" },
                  ];
                  let cumAngle = -90;
                  return slices.map((s, i) => {
                    if (s.pct <= 0) return null;
                    const startAngle = cumAngle;
                    const sweep = s.pct * 360;
                    cumAngle += sweep;
                    const startRad = (startAngle * Math.PI) / 180;
                    const endRad = ((startAngle + sweep) * Math.PI) / 180;
                    const x1 = cx + r * Math.cos(startRad);
                    const y1 = cy + r * Math.sin(startRad);
                    const x2 = cx + r * Math.cos(endRad);
                    const y2 = cy + r * Math.sin(endRad);
                    const largeArc = sweep > 180 ? 1 : 0;
                    const midRad = ((startAngle + sweep / 2) * Math.PI) / 180;
                    const tx = cx + r * 0.6 * Math.cos(midRad);
                    const ty = cy + r * 0.6 * Math.sin(midRad);
                    return (
                      <g key={i}>
                        <path
                          d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                          fill={s.color}
                          stroke="oklch(0.14 0.03 260)"
                          strokeWidth="2"
                        />
                        {s.pct > 0.08 && (
                          <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="11" fontWeight="bold">
                            {s.label}
                          </text>
                        )}
                      </g>
                    );
                  });
                })()}
              </svg>
              <div className="flex flex-wrap gap-4 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#3b82f6]" />
                  <span>Creator <span className="text-muted-foreground">${creatorEarn.toFixed(2)} ({creatorPct}%)</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#ec4899]" />
                  <span>Promoters <span className="text-muted-foreground">${promoterEarn.toFixed(2)} ({promoterPct}%)</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#0ea5e9]" />
                  <span>Winners <span className="text-neon font-bold">${winnerEarn.toFixed(2)} ({100 - PLATFORM_FEE - creatorPct - promoterPct}%)</span></span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Promoter pool is split pro-rata based on tickets sold per promoter
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── How Everyone Earns (replaces fake tickers) ─── */
function HowEveryoneEarns() {
  const betaStatus = trpc.coupon.betaStatus.useQuery(undefined, { staleTime: 5 * 60_000 });
  const betaActive = betaStatus.data?.active === true;
  const earners = [
    {
      role: "Players",
      icon: Trophy,
      color: "text-[#0ea5e9]",
      bg: "bg-[#0ea5e9]/10",
      border: "border-[#0ea5e9]/30",
      desc: "Win the largest share of the prize pool. The creator decides how prizes are distributed across placements (1st, 2nd, 3rd, etc.).",
      example: "1st place: 40% | 2nd: 20% | 3rd: 10% of the winner pool",
    },
    {
      role: "Creators",
      icon: Gamepad2,
      color: "text-[#3b82f6]",
      bg: "bg-[#3b82f6]/10",
      border: "border-[#3b82f6]/30",
      desc: "Host events and earn a creator share (up to 15%) of every ticket sold. Use our AI to build clearly structured, fairly judged, viral-ready events.",
      example: "Example: 100 tickets x $20 x 10% creator share = $200 per event",
    },
    {
      role: "Promoters",
      icon: Share2,
      color: "text-[#ec4899]",
      bg: "bg-[#ec4899]/10",
      border: "border-[#ec4899]/30",
      desc: "Every ticket buyer gets a unique link. Sell tickets through your link and earn a pro-rata share of the promoter pool. Sell 80% of tickets? Earn 80% of the promoter fund.",
      example: "Sell 80 of 100 tickets = 80% of the promoter pool",
    },
    {
      role: "GamePoint",
      icon: Shield,
      color: "text-neon",
      bg: "bg-neon/10",
      border: "border-neon/30",
      desc: betaActive
        ? "The platform takes a flat 15% of every event's ticket sales to cover operations, payment processing, and infrastructure. The $20 fee to create an event is waived during the beta with code BETA."
        : "The platform takes a flat 15% of every event's ticket sales to cover operations, payment processing, and infrastructure.",
      example: betaActive ? "15% platform fee · event fee waived in beta" : "15% platform fee",
    },
  ];

  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground uppercase mb-4">
            Four Ways to <span className="text-neon">Earn</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every ticket grows the prize pool, and everyone who makes the event happen gets a share: winners take the prize pool, creators earn a hosting share, promoters earn the promoter cut, and the platform sustains itself.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {earners.map((e, i) => (
            <div key={i} className={`feature-card border ${e.border}`}>
              <div className={`w-12 h-12 rounded-lg ${e.bg} flex items-center justify-center mb-4`}>
                <e.icon className={`w-6 h-6 ${e.color}`} />
              </div>
              <h3 className={`font-bold ${e.color} mb-2 text-lg`}>{e.role}</h3>
              <p className="text-sm text-muted-foreground mb-3">{e.desc}</p>
              <div className="bg-navy-light/50 rounded-lg p-3 border-l-2 border-neon/40">
                <p className="text-xs text-neon/80 italic">{e.example}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Creator Earnings Calculator ─── */
function CreatorEarningsCalculator() {
  const [ticketPrice, setTicketPrice] = useState(20);
  const [playersPerGame, setPlayersPerGame] = useState(100);
  const [gamesPerMonth, setGamesPerMonth] = useState(4);
  const [creatorShare, setCreatorShare] = useState(10);

  const revenuePerGame = ticketPrice * playersPerGame * (creatorShare / 100);
  const monthlyRevenue = revenuePerGame * gamesPerMonth;
  const yearlyRevenue = monthlyRevenue * 12;

  return (
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-extrabold text-center text-foreground mb-4">
          Creator Earnings Calculator
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          See how much you could earn as an event creator. Adjust the sliders to match your niche and audience size.
        </p>
        <div className="glass-panel rounded-2xl p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Ticket price: ${ticketPrice}</label>
                <input type="range" min={1} max={1000} value={ticketPrice}
                  onChange={(e) => setTicketPrice(Number(e.target.value))}
                  className="w-full accent-neon" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Players Per Event: {playersPerGame}</label>
                <input type="range" min={10} max={10000} step={10} value={playersPerGame}
                  onChange={(e) => setPlayersPerGame(Number(e.target.value))}
                  className="w-full accent-neon" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Events Per Month: {gamesPerMonth}</label>
                <input type="range" min={1} max={30} value={gamesPerMonth}
                  onChange={(e) => setGamesPerMonth(Number(e.target.value))}
                  className="w-full accent-neon" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Your Creator Share: {creatorShare}%</label>
                <input type="range" min={1} max={15} value={creatorShare}
                  onChange={(e) => setCreatorShare(Number(e.target.value))}
                  className="w-full accent-neon" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-navy-light rounded-xl p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">Per Event</p>
                <p className="text-3xl font-extrabold text-neon">${revenuePerGame.toLocaleString()}</p>
              </div>
              <div className="bg-navy-light rounded-xl p-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">Monthly</p>
                <p className="text-3xl font-extrabold text-foreground">${monthlyRevenue.toLocaleString()}</p>
              </div>
              <div className="bg-navy-light rounded-xl p-6 text-center border border-neon/30">
                <p className="text-sm text-muted-foreground mb-1">Annual Potential</p>
                <p className="text-4xl font-extrabold text-neon">${yearlyRevenue.toLocaleString()}</p>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Plus promoter earnings if you also share your own event links
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Home Page ─── */
export default function Home() {
  const { isAuthenticated, user, loading } = useAuth();
  const [, setLocation] = useLocation();
  const betaStatus = trpc.coupon.betaStatus.useQuery(undefined, { staleTime: 5 * 60_000 });
  const betaActive = betaStatus.data?.active === true;

  // Redirect newly-verified users to complete their profile
  useEffect(() => {
    if (!loading && user && user.profileComplete === 0) {
      setLocation("/profile-setup");
    }
  }, [loading, user, setLocation]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicNav />

      {/* ═══ HERO: who's who + the prize pool, up front ═══ */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-blue-accent/10" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-neon/5 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-accent/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full relative grid lg:grid-cols-5 gap-8 items-start">
          {/* Left: the promise */}
          <div className="lg:col-span-3 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neon/10 border border-neon/20 text-neon text-xs font-bold uppercase tracking-widest">
              <Zap className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Skill events where everyone gets a share</span><span className="sm:hidden">Everyone gets a share</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight">
              <span className="block text-gold">HOST IT.</span>
              <span className="block text-blue-400">SHARE IT.</span>
              <span className="block text-neon">WIN IT.</span>
            </h1>

            <p className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Think of a tournament anyone can run: the <b className="text-foreground">creator</b> hosts it,{" "}
              <b className="text-foreground">promoters</b> sell the tickets, <b className="text-foreground">players</b> compete. Every ticket grows the prize pool, and when results post it is split
              between the winners, the creator and the promoters.
            </p>

            <div className="grid grid-cols-3 gap-2 lg:hidden">
              {roleCards.map((r) => (
                <div key={r.key} className={`rounded-lg border p-2.5 ${r.ring}`}>
                  <p className={`text-xs font-extrabold ${r.color}`}>{r.label}</p>
                  <p className="text-[11px] leading-snug text-foreground mt-0.5">{r.short}</p>
                </div>
              ))}
            </div>

            <div className="hidden sm:flex flex-wrap gap-2">
              {[
                { icon: Zap, text: "Events run for hours or days, not months" },
                { icon: Share2, text: "Every player gets a share link" },
                { icon: Trophy, text: "Prize pool split when results post" },
              ].map((chip) => (
                <span key={chip.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-navy-light border border-border/50 text-xs font-medium text-foreground">
                  <chip.icon className="w-3.5 h-3.5 text-neon" /> {chip.text}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link href="/create">
                <Button className="bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide px-8 py-6 text-base">
                  Start an event
                </Button>
              </Link>
              <Link href="/games">
                <Button variant="outline" className="border-neon/30 text-neon hover:bg-neon/10 font-bold uppercase tracking-wide px-8 py-6 text-base">
                  Find an event
                </Button>
              </Link>
              <Link href="/promoters">
                <Button variant="outline" className="border-blue-400/30 text-blue-400 hover:bg-blue-400/10 font-bold uppercase tracking-wide px-8 py-6 text-base">
                  Promote events
                </Button>
              </Link>
            </div>

            {betaActive && (
              <p className="text-sm text-muted-foreground">
                <span className="font-bold text-neon">Beta:</span> creating an event is free. The $20 event fee is waived with code{" "}
                <b className="text-foreground">BETA</b>.
              </p>
            )}
          </div>

          {/* Right: who's who, and where every ticket goes */}
          <div className="lg:col-span-2 space-y-4">
            <div className="glass-panel rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-neon">Who's who</h3>
              <div className="space-y-3">
                {roleCards.map((r) => (
                  <div key={r.key} className="flex gap-3 items-start">
                    <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${r.ring}`}>
                      <r.icon className={`w-5 h-5 ${r.color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-bold ${r.color}`}>{r.label}</p>
                      <p className="text-xs text-muted-foreground">{r.what}</p>
                      <p className="text-xs font-semibold text-foreground mt-0.5">{r.gets}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-panel rounded-xl p-5">
              <h3 className="text-xs font-bold uppercase tracking-widest text-neon mb-3">Where every ticket goes</h3>
              <div className="flex h-9 rounded-lg overflow-hidden text-[11px] font-extrabold">
                {splitBar.map((b) => (
                  <div key={b.label} className={`flex items-center justify-center ${b.cls}`} style={{ width: `${b.pct}%` }}>
                    {b.pct}%
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                {splitBar.map((b) => (
                  <span key={b.label} className="flex items-center gap-1.5">
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm ${b.cls.split(" ")[0]}`} /> {b.label} {b.pct}%
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                Default split of ticket sales. Creators can raise their share to 15%. Ticket prices run from $1 to $10,000.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOUR WAYS TO EARN (replaces fake stats) ═══ */}
      <HowEveryoneEarns />

      {/* ═══ PRIZE POOL CALCULATOR ═══ */}
      <PrizeCalculator />

      {/* ═══ CREATOR EARNINGS CALCULATOR ═══ */}
      <CreatorEarningsCalculator />

      {/* ═══ USE CASES — "Works for Everything, Everywhere" ═══ */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-foreground uppercase mb-4">
              Works for <span className="text-neon">Everything</span>, Everywhere
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From cooking competitions in Texas to coding challenges in Tokyo — GamePoint powers skill-based competitions for any niche, any audience, anywhere in the world. The categories are infinite.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {useCases.map((uc, i) => (
              <div key={i} className="feature-card group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-neon/10 flex items-center justify-center">
                    <uc.icon className="w-5 h-5 text-neon" />
                  </div>
                  <h3 className="font-bold text-foreground">{uc.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{uc.desc}</p>
                <div className="bg-navy-light/50 rounded-lg p-3 border-l-2 border-neon/40">
                  <p className="text-xs text-neon/80 italic">{uc.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <h2 className="text-4xl md:text-5xl font-extrabold text-foreground uppercase">Platform Features</h2>
            <p className="text-lg text-muted-foreground mt-2">Everything you need to create, compete, and earn</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {features.map((f, i) => (
              <div key={i} className="feature-card">
                <div className="w-12 h-12 rounded-lg bg-neon/10 border border-neon/20 flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-neon" />
                </div>
                <h3 className="font-bold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground uppercase mb-2">How It Works</h2>
          <p className="text-lg text-muted-foreground mb-12">Start competing in just 3 steps</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: 1, title: "SIGN UP", desc: "Create your account in seconds. You're instantly ready to play, create, or promote." },
              { step: 2, title: "JOIN OR CREATE", desc: betaActive ? "Browse events or create your own with the AI event builder (creating an event is free with code BETA)." : "Browse events or create your own with the AI event builder." },
              { step: 3, title: "COMPETE & EARN", desc: "Buy a ticket, post your score, share your link to earn as a promoter, and win prizes." },
            ].map((s) => (
              <div key={s.step} className="feature-card text-center">
                <div className="step-badge mx-auto mb-4">{s.step}</div>
                <h3 className="font-extrabold text-foreground uppercase mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground uppercase mb-4">
            Ready to <span className="text-neon">Compete</span>?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Host it, share it or win it. Every ticket grows the prize pool, and everyone who makes the event happen gets a share.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/games">
              <Button className="bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide px-8 py-6 text-base">
                FIND AN EVENT
              </Button>
            </Link>
            <Link href="/create">
              <Button variant="outline" className="border-neon/30 text-neon hover:bg-neon/10 font-bold uppercase tracking-wide px-8 py-6 text-base">
                START AN EVENT
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
